import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useI18n } from "@wato/i18n/react";
import { Button } from "@wato/ui";
import { useApi } from "../../api/context";
import { useAuth } from "../../auth/AuthProvider";
import { PresenceIndicator } from "../inbox/agent-list/PresenceIndicator";
import { getSupportToken } from "./support-session";

/**
 * OWN-137: the signed-in agent's own busy / available switch (inside the avatar menu). Busy keeps
 * the agent out of auto-assignment; the heartbeat keeps the state until they switch back.
 */
export function AvailabilityToggle({ workspaceId }: { workspaceId: string }) {
  const { t } = useI18n();
  const api = useApi();
  const qc = useQueryClient();
  const { state } = useAuth();
  const userId = state.status === "ready" ? state.me.user.id : null;
  const key = ["agent-presence", workspaceId];
  const presence = useQuery({
    queryKey: key,
    queryFn: () => api.listAgentPresence(workspaceId),
    enabled: userId !== null && !getSupportToken(),
  });
  const set = useMutation({
    mutationFn: (availability: "available" | "busy") =>
      api.setAgentAvailability(workspaceId, availability),
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
  if (userId === null || getSupportToken()) return null;
  const busy = presence.data?.find((a) => a.userId === userId)?.status === "busy";
  return (
    <div className="border-t border-line px-3 py-2">
      <p className="pb-2 text-xs font-semibold text-fg-muted">
        {t("routing.presence.availabilityLabel")}
      </p>
      <div className="flex items-center gap-2">
        <PresenceIndicator status={busy ? "busy" : "online"} />
        <Button
          size="sm"
          variant="secondary"
          disabled={set.isPending}
          aria-pressed={busy}
          onClick={() => set.mutate(busy ? "available" : "busy")}
        >
          {busy ? t("routing.presence.setAvailable") : t("routing.presence.setBusy")}
        </Button>
      </div>
    </div>
  );
}
